import { config } from '@ps/config/client';
import { auth } from '@ps/functions/firebase/auth';

import type { MediaCategory } from '../types/MediaCategory';
import type { UploadProgress } from '../types/UploadProgress';
import type { RequestPresignedUrlMetadata } from './requestPresignedUrl';

/**
 * Smallest chunk size accepted by the multipart endpoint. The
 * service rejects parts smaller than this except for the final chunk.
 */
export const kMinimumChunkSizeBytes = 20 * 1024 * 1024; // 20MB

/**
 * Default chunk size. PopshopRN uses 50MB on both mobile and desktop;
 * we mirror that. Bumped up here, each PUT round-trip is more
 * efficient; smaller, the failure radius of a retry shrinks.
 */
export const kDefaultChunkSizeBytes = 50 * 1024 * 1024; // 50MB

/**
 * Concurrent in-flight chunks. PopshopRN uses 5 on mobile / 10 on
 * desktop; we default to a safe middle-ground and let the caller
 * override.
 */
export const kDefaultConcurrency = 6;

export interface UploadMultipartOptions {
  /** Source file (or blob). */
  file: Blob;
  /** Original filename — recorded on the gallery row. */
  filename: string;
  /** Server-side category that drives metadata persistence. */
  category: MediaCategory;
  /** Free-form metadata, same shape as the single-PUT flow. */
  metadata: RequestPresignedUrlMetadata;
  /** Override chunk size in bytes. Clamped to `kMinimumChunkSizeBytes`. */
  chunkSize?: number;
  /** Override concurrent in-flight chunks. */
  concurrency?: number;
  /** Per-chunk retry limit (default 3). */
  retryLimit?: number;
  /** Fires with overall progress aggregated across all chunks. */
  onProgress?: (progress: UploadProgress) => void;
  /** Aborts the upload between chunk batches and inside any in-flight PUT. */
  signal?: AbortSignal;
}

export interface UploadedMultipartResult {
  /** CDN URL where the asset will be reachable post-upload. */
  cdnUrl: string;
  /** Media-gallery row id. */
  objectId: string;
  /** Multipart upload id (only useful for diagnostic logging). */
  uploadId: string;
}

interface MultipartInitResponse {
  uploadId: string;
  preSignedUrls: string[];
  image?: GalleryItemPayload;
  clip?: GalleryItemPayload;
}

interface GalleryItemPayload {
  id: string;
  cdnUrl: string;
  metaIdentifier: string;
  metaData: {
    imageURL?: string;
    videoURL?: string;
  };
}

interface UploadedPart {
  PartNumber: number;
  ETag: string;
}

/**
 * Multipart upload primitive for files ≥ ~20MB. Initiates a multipart
 * session against the media-gallery service, uploads chunks in
 * parallel batches with per-chunk retries, and finalizes when all
 * parts succeed.
 *
 * Mirrors PopshopRN's `useMultiChunkUpload()` but standalone (not a
 * hook) so it composes inside `useMediaUpload` and non-React batch
 * pipelines.
 */
export async function uploadMultipart(
  options: UploadMultipartOptions,
): Promise<UploadedMultipartResult> {
  const {
    file,
    filename,
    category,
    metadata,
    onProgress,
    signal,
    retryLimit = 3,
    concurrency = kDefaultConcurrency,
  } = options;

  const chunkSize = Math.max(
    options.chunkSize ?? kDefaultChunkSizeBytes,
    kMinimumChunkSizeBytes,
  );

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const token = await auth().currentUser?.getIdToken();
  if (!token) throw new Error('User is not authenticated');

  // Compute chunk plan. If the file is small enough to fit in a
  // single chunk we still slice it (the multipart endpoint requires
  // at least one part) but never below the minimum.
  let totalChunks = Math.ceil(file.size / chunkSize);
  if (totalChunks === 0) totalChunks = 1;

  const init = await initMultipart({
    token,
    filename,
    category,
    metadata,
    totalChunks,
    signal,
  });

  const item = init.image ?? init.clip;
  if (!item) {
    throw new Error('Multipart init response missing image/clip payload');
  }
  const assetPath = item.metaData.imageURL ?? item.metaData.videoURL;
  if (!assetPath) {
    throw new Error('Multipart init response missing asset path');
  }

  const cdnUrl = `${item.cdnUrl}${assetPath}`;
  const objectId = item.metaIdentifier;

  let loadedBytes = 0;
  const parts: UploadedPart[] = [];

  for (
    let batchStart = 0;
    batchStart < totalChunks;
    batchStart += concurrency
  ) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const batchTasks: Promise<UploadedPart>[] = [];

    for (
      let offset = 0;
      offset < concurrency && batchStart + offset < totalChunks;
      offset++
    ) {
      const partIndex = batchStart + offset;
      const start = partIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      const partNumber = partIndex + 1;
      const presignedUrl = init.preSignedUrls[partIndex];

      batchTasks.push(
        uploadChunkWithRetry({
          chunk,
          presignedUrl,
          partNumber,
          contentType: metadata.contentType ?? file.type,
          retryLimit,
          signal,
          onPartProgress: deltaBytes => {
            loadedBytes += deltaBytes;
            onProgress?.({
              loaded: loadedBytes,
              total: file.size,
              ratio: file.size > 0 ? Math.min(1, loadedBytes / file.size) : 0,
            });
          },
          onPartRollback: deltaBytes => {
            loadedBytes -= deltaBytes;
          },
        }),
      );
    }

    const batchResults = await Promise.all(batchTasks);
    parts.push(...batchResults);
  }

  await completeMultipart({
    token,
    objectId,
    userId: item.id,
    uploadId: init.uploadId,
    parts,
  });

  onProgress?.({ loaded: file.size, total: file.size, ratio: 1 });

  return { cdnUrl, objectId, uploadId: init.uploadId };
}

async function initMultipart({
  token,
  filename,
  category,
  metadata,
  totalChunks,
  signal,
}: {
  token: string;
  filename: string;
  category: MediaCategory;
  metadata: RequestPresignedUrlMetadata;
  totalChunks: number;
  signal?: AbortSignal;
}): Promise<MultipartInitResponse> {
  const response = await fetch(
    `${config.awsMediaGalleryBaseUrl}/multipartUpload`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({
        fileName: filename,
        numberOfChunks: totalChunks,
        fileMeta: { fileCategory: category },
        metaData: {
          mediatype: metadata.contentType,
          size: metadata.size,
          tag: metadata.tag,
          createdBy: metadata.createdBy,
          createdAt: new Date().toISOString(),
          internalPostId: metadata.internalPostId,
          internalMessageId: metadata.internalMessageId,
          dimensions:
            metadata.imageMetadata?.width != null &&
            metadata.imageMetadata.height != null
              ? {
                  width: metadata.imageMetadata.width,
                  height: metadata.imageMetadata.height,
                }
              : undefined,
          blurhash: metadata.imageMetadata?.blurhash,
        },
      }),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `Multipart upload init failed: ${response.status} ${response.statusText}`,
    );
  }

  return (await response.json()) as MultipartInitResponse;
}

async function uploadChunkWithRetry({
  chunk,
  presignedUrl,
  partNumber,
  contentType,
  retryLimit,
  signal,
  onPartProgress,
  onPartRollback,
}: {
  chunk: Blob;
  presignedUrl: string;
  partNumber: number;
  contentType: string | undefined;
  retryLimit: number;
  signal?: AbortSignal;
  onPartProgress: (deltaBytes: number) => void;
  onPartRollback: (deltaBytes: number) => void;
}): Promise<UploadedPart> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retryLimit; attempt++) {
    let chunkLoaded = 0;
    try {
      const etag = await putChunkToS3({
        chunk,
        presignedUrl,
        contentType,
        signal,
        onProgress: deltaBytes => {
          chunkLoaded += deltaBytes;
          onPartProgress(deltaBytes);
        },
      });
      return { PartNumber: partNumber, ETag: etag };
    } catch (error) {
      // Roll back the per-chunk bytes so the aggregated ratio doesn't
      // drift after a retry.
      onPartRollback(chunkLoaded);
      lastError = error;
      if (signal?.aborted) throw error;
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error(`Chunk ${partNumber} failed after ${retryLimit + 1} attempts`);
}

function putChunkToS3({
  chunk,
  presignedUrl,
  contentType,
  signal,
  onProgress,
}: {
  chunk: Blob;
  presignedUrl: string;
  contentType: string | undefined;
  signal?: AbortSignal;
  onProgress: (deltaBytes: number) => void;
}): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();
    let lastLoaded = 0;

    xhr.upload.onprogress = event => {
      if (!event.lengthComputable) return;
      const delta = event.loaded - lastLoaded;
      lastLoaded = event.loaded;
      if (delta > 0) onProgress(delta);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag =
          xhr.getResponseHeader('ETag') ?? xhr.getResponseHeader('etag') ?? '';
        resolve(etag.replace(/"/g, ''));
      } else {
        reject(
          new Error(`Chunk upload failed: ${xhr.status} ${xhr.statusText}`),
        );
      }
    };

    xhr.onerror = () => reject(new Error('Network error during chunk upload'));
    xhr.onabort = () =>
      reject(new DOMException('Chunk upload aborted', 'AbortError'));

    if (signal) {
      const onAbort = () => xhr.abort();
      signal.addEventListener('abort', onAbort, { once: true });
    }

    xhr.open('PUT', presignedUrl, true);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(chunk);
  });
}

async function completeMultipart({
  token,
  objectId,
  userId,
  uploadId,
  parts,
}: {
  token: string;
  objectId: string;
  userId: string;
  uploadId: string;
  parts: UploadedPart[];
}): Promise<void> {
  const response = await fetch(
    `${config.awsMediaGalleryBaseUrl}/completeMultipartUpload`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({
        uploadId,
        metaIdentifier: objectId,
        id: userId,
        parts: parts.map(part => ({
          PartNumber: part.PartNumber,
          ETag: part.ETag,
        })),
      }),
    },
  );

  if (!response.ok) {
    throw new Error(
      `Multipart upload completion failed: ${response.status} ${response.statusText}`,
    );
  }
}
